import { DestinationTypeEnum } from "../enums/destination";
import { EngineEnum } from "../enums/engine";
import { ImageEngineEnum } from "../enums/imageEngine";
import {
  ArticleTypeEnum,
  BrandPersonaEnum,
  BrandToneEnum,
  BrandVoiceEnum,
  FaqQuestionPrefixEnum,
  ImageSizeEnum,
  ImageStyleEnum,
  ImageToneEnum,
  NoOfExternalLinkEnum,
  PointOfViewEnum,
  SourceEnum,
  TemplateStatusEnum,
  TranscribeSubjectEnum,
} from "../enums/template";

export type DestinationInfo = {
  destinationId: string;
  destinationTypeId: DestinationTypeEnum;
};

export class Template {
  // Backend only
  includeIntro: boolean;
  mediaAuthorName: string;

  templateId: string | null;
  name: string | null;

  // Platform
  sourceId: SourceEnum;

  // Template status
  templateStatusId!: TemplateStatusEnum;

  // Media link
  mediaUrl: string | null;
  uploadMediaUrl: string | null; // frontend only
  rssFeedUrls: string | null;
  textBlock: string | null;

  // Auto
  isAutoEnabled: boolean;
  username: string | null;
  externalUserId: string | null; // instagram only
  autoDays: number[] | null;
  autoTime1: string | null;
  autoTime2: string | null;
  profileUrl: string | null;
  timezone: string;

  // RSS Feed Selection
  rssGetRandomEntry: boolean | null;

  // Include image
  includeImage: boolean;
  imageSizeId: ImageSizeEnum | null;
  imageStyleId: ImageStyleEnum | null;
  imageToneId: ImageToneEnum | null;

  // Include External Link
  externalLinkCount: number;

  // Include web embed
  includeWebEmbed: boolean;

  // Include FAQs
  includeFaq: boolean;
  faqCount: number | null;
  faqQuestionPrefixId: FaqQuestionPrefixEnum | null;

  // Article type
  articleTypeId: number;

  // Title type
  titleTypeId: number;

  // Point of view
  pointOfViewId: PointOfViewEnum | null;
  isSpeakerTheAuthor: boolean | null;
  speakerName: string | null;
  speakerAbout: string | null;

  // Brand settings
  brandPersonaId: BrandPersonaEnum | null;
  brandVoiceId: BrandVoiceEnum | null;
  brandToneId: BrandToneEnum | null;

  // Include credits
  includeCredits: boolean;
  bio: string;
  includeSocialLinks: boolean | null;

  // Additional context
  additionalContext: string | null;

  // Primary subject of content
  transcribeSubjectId: TranscribeSubjectEnum;

  // Creativity
  temperature: number;

  // Destination
  destinationId: string | null;
  oldDestinationId: string | null;
  destinationInfoList: DestinationInfo[] | null;

  // AI model to use
  engineId: EngineEnum | null;
  imageEngineId: ImageEngineEnum | null;

  // Image engine negative prompts
  excludeImageText: boolean | null;
  excludeImageFaces: boolean | null;

  // Top items
  topItemsTopic: string | null;
  topItemsCount: number | null;

  // Best items
  bestItemsTopic: string | null;
  bestItemsCount: number | null;

  accountTagId: string | null;
  constructor({
    includeIntro,
    mediaAuthorName,
    templateId,
    name,
    sourceId,
    templateStatusId,
    mediaUrl,
    uploadMediaUrl,
    rssFeedUrls,
    textBlock,
    isAutoEnabled,
    username,
    externalUserId,
    autoDays,
    autoTime1,
    autoTime2,
    profileUrl,
    timezone,
    rssGetRandomEntry,
    includeImage,
    imageSizeId,
    imageStyleId,
    imageToneId,
    includeWebEmbed,
    externalLinkCount,
    includeFaq,
    faqCount,
    faqQuestionPrefixId,
    articleTypeId,
    titleTypeId,
    pointOfViewId,
    isSpeakerTheAuthor,
    speakerName,
    speakerAbout,
    brandPersonaId,
    brandVoiceId,
    brandToneId,
    includeCredits,
    bio,
    includeSocialLinks,
    additionalContext,
    transcribeSubjectId,
    temperature,
    destinationId,
    oldDestinationId,
    destinationInfoList,
    engineId,
    imageEngineId,
    excludeImageText,
    excludeImageFaces,
    topItemsTopic,
    topItemsCount,
    bestItemsTopic,
    bestItemsCount,
    accountTagId,
  }: any) {
    this.includeIntro = includeIntro || false;
    this.mediaAuthorName = mediaAuthorName || null;
    this.templateId = templateId || null;
    this.name = name || null;
    this.sourceId = sourceId || SourceEnum.INSTAGRAM_REELS;
    this.templateStatusId = templateStatusId ?? TemplateStatusEnum.PENDING;
    this.mediaUrl = mediaUrl || null;
    this.uploadMediaUrl = uploadMediaUrl || null; // frontend-only
    this.rssFeedUrls = rssFeedUrls || null;
    this.textBlock = textBlock || null;
    this.isAutoEnabled = isAutoEnabled || false;
    this.username = username || null;
    this.externalUserId = externalUserId || null; // Instagram-only
    this.autoDays = autoDays || [];
    this.autoTime1 = autoTime1 || null;
    this.autoTime2 = autoTime2 || null;
    this.profileUrl = profileUrl || null;
    this.timezone = timezone || null;
    this.rssGetRandomEntry = rssGetRandomEntry || false;
    this.includeImage = includeImage || false;
    this.imageSizeId = imageSizeId || null;
    this.imageStyleId = imageStyleId || null;
    this.imageToneId = imageToneId || null;
    this.includeWebEmbed = includeWebEmbed || false;
    this.externalLinkCount = externalLinkCount || NoOfExternalLinkEnum.ZERO;
    this.includeFaq = includeFaq || false;
    this.faqCount = faqCount || null;
    this.faqQuestionPrefixId = faqQuestionPrefixId || null;
    this.articleTypeId = articleTypeId || ArticleTypeEnum.STANDARD_ARTICLE;
    this.titleTypeId = titleTypeId;
    this.pointOfViewId = pointOfViewId || PointOfViewEnum.THIRD_PERSON;
    this.isSpeakerTheAuthor = isSpeakerTheAuthor || false;
    this.speakerName = speakerName || null;
    this.speakerAbout = speakerAbout || null;
    this.brandPersonaId = brandPersonaId || null;
    this.brandVoiceId = brandVoiceId || null;
    this.brandToneId = brandToneId || null;
    this.includeCredits = includeCredits || false;
    this.bio = bio || null;
    this.includeSocialLinks = includeSocialLinks || false;
    this.additionalContext = additionalContext || null;
    this.transcribeSubjectId =
      transcribeSubjectId || TranscribeSubjectEnum.GENERAL;
    this.temperature = temperature ?? 0.5;
    this.destinationId = destinationId || null;
    this.oldDestinationId = oldDestinationId || null;
    this.destinationInfoList = destinationInfoList || null;
    this.engineId = engineId || null;
    this.imageEngineId = imageEngineId || null;
    this.excludeImageText = excludeImageText || null;
    this.excludeImageFaces = excludeImageFaces || null;
    this.topItemsTopic = topItemsTopic || null;
    this.topItemsCount = topItemsCount || null;
    this.accountTagId = accountTagId || null;
    this.bestItemsTopic = bestItemsTopic || null;
    this.bestItemsCount = bestItemsCount || null;
  }
}

export class TemplateDetails extends Template {
  runTimestamp: string | null;
  completedAt: string | null;
  archivedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  articleCount: number;
  accountTagName: string | null;

  constructor({
    runTimestamp,
    completedAt,
    archivedAt,
    createdBy,
    updatedBy,
    createdAt,
    articleCount,
    params,
    accountTagName,
  }: any) {
    super(params);
    this.runTimestamp = runTimestamp;
    this.completedAt = completedAt;
    this.archivedAt = archivedAt;
    this.createdBy = createdBy;
    this.updatedBy = updatedBy;
    this.createdAt = createdAt;
    this.articleCount = articleCount;
    this.accountTagName = accountTagName;
  }
}
